import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { REHYDRATE } from "redux-persist";
import { Guid } from "guid-typescript";

import store, { IAppState } from "../../state/Store";
import AlertEntry, {
    Alert,
    AlertResult,
    AlertTypes,
} from "../../types/system/AlertEntry";
import AlertDialogPrompt from "../../types/system/AlertDialogPrompt";
import Utilities from "../../foundation/Utility";
import ServerError from "../../types/system/ServerError";

// Define the type for our state store entry
type AlertsState = {
    open: boolean;
    current: AlertEntry;
    alerts: AlertEntry[];
};

// Define the initial state for our state store entry
const initialState: AlertsState = {
    open: false,
    current: null,
    alerts: [],
};

// Create a slice to hold all our reducers
const alertSlice = createSlice({
    name: "alerts",
    initialState: initialState,
    reducers: {
        setOpen: (state, action: PayloadAction<boolean>) => {
            state.open = action.payload;
        },
        addAlertReducer: (state, action: PayloadAction<AlertEntry>) => {
            state.alerts = [...state.alerts, action.payload];
        },
        moveNextAlertReducer: state => {
            const next = state.alerts[0];

            if (
                next.type === AlertTypes.Prompt &&
                (next.prompts?.length ?? 0) === 0
            ) {
                next.prompts = next.prompts ?? [];
                next.prompts.push({
                    type: "text",
                    display: next.text,
                } as AlertDialogPrompt);
                next.text = "";
            }

            for (const [index, prompt] of (next.prompts ?? []).entries()) {
                prompt.id = Guid.create();
                prompt.order = index;
            }

            state.current = next;
            state.alerts = state.alerts.slice(1);
            state.open = !!state.current;
        },
        updatePrompt: (state, action: PayloadAction<UpdatePromptType>) => {
            const prompt = state.current.prompts.find(
                item => item.id === action.payload.prompt.id,
            );
            prompt.value = action.payload.value;
            state.current = {
                ...state.current,
                prompts: [
                    ...state.current.prompts.filter(
                        item => item.id !== action.payload.prompt.id,
                    ),
                    { ...prompt, value: action.payload.value },
                ].sort((a, b) => a.order - b.order),
            };
        },
    },
    extraReducers(builder) {
        builder.addCase(REHYDRATE, (_, rawData: any) => {
            if (rawData?.key !== "tts") return undefined;

            return {
                open: false,
                current: null,
                alerts: [],
            };
        });
    },
});

export interface UpdatePromptType {
    prompt: AlertDialogPrompt;
    value: any;
}

const { setOpen, addAlertReducer, moveNextAlertReducer } = alertSlice.actions;

export const { updatePrompt } = alertSlice.actions;

export const handleError = createAsyncThunk(
    "alerts/handleError",
    async (payload: any, thunkAPI) => {
        const error = payload as ServerError;
        thunkAPI.dispatch(
            addAlertInternal({
                title: error.reason ?? "Error",
                type: AlertTypes.Alert,
                icon: "error",
                styleColor: "red",
                text:
                    error.exceptionMessage ??
                    error.message ??
                    "An unknown error has occurred",
            } as AlertEntry),
        );
    },
);
export const addAlert = async (payload: AlertEntry): Promise<AlertResult> => {
    return await store.dispatch(addAlertInternal(payload)).unwrap();
};
const addAlertInternal = createAsyncThunk(
    "alerts/addAlertInternal",
    async (payload: AlertEntry, thunkAPI) => {
        const { alert } = store.getState().db;

        payload.type = payload.type ?? AlertTypes.Alert;
        const newAlert = new Alert(payload);
        thunkAPI.dispatch(addAlertReducer(newAlert));
        if (!alert.open) thunkAPI.dispatch(moveNextAlert());
        return newAlert.promiseWrapper.promise;
    },
);
export const moveNextAlert = createAsyncThunk(
    "alerts/moveNextAlert",
    async (_, thunkAPI) => {
        const { alert } = store.getState().db;

        let wasOpen = alert.open;

        thunkAPI.dispatch(setOpen(false));
        if (wasOpen) await Utilities.waitFor(500);
        thunkAPI.dispatch(moveNextAlertReducer());
    },
);

// We have to export the reducer as this is added to the state store
export default alertSlice.reducer;
